import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { Loader2, Link2, Undo2, Repeat } from 'lucide-react';

interface UnclaimedSim {
  simSubscriptionId: string;
  deviceInstallId: string | null;
  carrierName: string | null;
  slotIndex: number | null;
  callCount: number;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  seenForUserIds: string[];
}

interface RegistrationRecord {
  id: string;
  simSubscriptionId: string;
  deviceInstallId: string | null;
  phoneNumber: string;
  userId: string;
  carrierName: string | null;
  slotIndex: number | null;
  status: string;
  registeredAt?: string | null;
  user?: { id: string; firstName?: string | null; lastName?: string | null } | null;
}

interface PersonOption { id: string; name: string }

// 🔒 Joining the two device-supplied ids with a separator collides:
// ('a','b::c') and ('a::b','c') both yield 'a::b::c', so two different SIMs
// would share one React key, one draft and one row error. The backend hit
// exactly this in sim-registry.routes.ts; same fix here.
const pairKey = (sim: string, dev: string | null | undefined) => JSON.stringify([sim, dev ?? null]);

// The create endpoint requires exactly 10 digits. Validating here is not
// politeness: reassignment is retire-then-create with no transaction, so a
// number the server will reject must never reach the retire call.
const isTenDigits = (s: string) => /^\d{10}$/.test(s.trim());

export function SimRegistryTab() {
  const [unclaimed, setUnclaimed] = useState<UnclaimedSim[]>([]);
  const [registrations, setRegistrations] = useState<RegistrationRecord[]>([]);
  const [people, setPeople] = useState<PersonOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Keyed by row: the server's own words stay pinned to the row they describe.
  // A 409 names the number the SIM is already bound to — a generic "failed"
  // throws away the only actionable part.
  const [rowError, setRowError] = useState<Record<string, string>>({});
  // Bind drafts, keyed by row. Reassign reuses the same draft shape so the
  // Registered section can open the identical form.
  const [draft, setDraft] = useState<Record<string, { phoneNumber: string; userId: string }>>({});
  const [reassigning, setReassigning] = useState<string | null>(null);
  // 🔒 Without this, a 404 / 500 / expired token renders the same confident
  // "No registered SIMs yet." as a genuinely empty registry — two different
  // causes reading identically on the one screen where that is invisible.
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      const [unclaimedRes, regRes] = await Promise.all([
        api.get('/telecaller/sim-registry/unclaimed'),
        api.get('/telecaller/sim-registry'),
      ]);
      setUnclaimed(unclaimedRes.data?.unclaimed ?? []);
      setRegistrations(regRes.data?.registrations ?? []);
      setLoadError(null);
    } catch (err: any) {
      setUnclaimed([]);
      setRegistrations([]);
      setLoadError(
        err?.response?.data?.error ||
          'The SIM registry could not be loaded. Nothing below is a statement about your SIMs.',
      );
    }
    try {
      const usersRes = await api.get('/users?limit=200');
      const list = (usersRes.data?.data?.users ?? usersRes.data?.data ?? []) as any[];
      setPeople(
        (Array.isArray(list) ? list : [])
          .filter(u => u?.isActive !== false)
          .map(u => ({
            id: u.id,
            name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
          })),
      );
    } catch {}
    setLoading(false);
  };

  useEffect(() => { void loadData(); }, []);

  const personName = (userId: string, fallback?: RegistrationRecord['user']) => {
    const fromList = people.find(p => p.id === userId)?.name;
    if (fromList) return fromList;
    const joined = `${fallback?.firstName ?? ''} ${fallback?.lastName ?? ''}`.trim();
    return joined || userId;
  };

  const draftFor = (key: string) => draft[key] ?? { phoneNumber: '', userId: '' };
  const setDraftField = (key: string, field: 'phoneNumber' | 'userId', value: string) =>
    setDraft(prev => ({ ...prev, [key]: { ...draftFor(key), [field]: value } }));

  const bind = async (
    key: string,
    sim: { simSubscriptionId: string; deviceInstallId: string | null; carrierName?: string | null; slotIndex?: number | null },
    retireFirstId?: string,
  ) => {
    const d = draftFor(key);
    if (!d.phoneNumber.trim() || !d.userId) return;
    if (!isTenDigits(d.phoneNumber)) {
      setRowError(prev => ({ ...prev, [key]: 'Enter the SIM\u2019s number as exactly 10 digits.' }));
      return;
    }
    if (!sim.deviceInstallId) {
      setRowError(prev => ({ ...prev, [key]: 'This SIM was reported without a device id, so it cannot be bound.' }));
      return;
    }
    setBusy(key);
    setRowError(prev => ({ ...prev, [key]: '' }));
    try {
      // 🔒 Reassignment is retire-then-create, NEVER an in-place userId update:
      // mutating it would silently rewrite who past MISMATCH calls were
      // measured against.
      if (retireFirstId) await api.post(`/telecaller/sim-registry/${retireFirstId}/retire`, {});
      await api.post('/telecaller/sim-registry', {
        simSubscriptionId: sim.simSubscriptionId,
        deviceInstallId: sim.deviceInstallId,
        phoneNumber: d.phoneNumber.trim(),
        userId: d.userId,
        carrierName: sim.carrierName ?? null,
        slotIndex: sim.slotIndex ?? null,
      });
      setDraft(prev => ({ ...prev, [key]: { phoneNumber: '', userId: '' } }));
      setReassigning(null);
      await loadData();
    } catch (err: any) {
      // Surface the server's message VERBATIM.
      setRowError(prev => ({ ...prev, [key]: err?.response?.data?.error || 'Failed to bind this SIM' }));
    } finally { setBusy(null); }
  };

  const retire = async (reg: RegistrationRecord) => {
    setBusy(reg.id);
    setRowError(prev => ({ ...prev, [reg.id]: '' }));
    try {
      await api.post(`/telecaller/sim-registry/${reg.id}/retire`, {});
      await loadData();
    } catch (err: any) {
      setRowError(prev => ({ ...prev, [reg.id]: err?.response?.data?.error || 'Failed to retire this registration' }));
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Loader2 size={16} className="animate-spin" /> Loading SIM registry...
      </div>
    );
  }

  const activeRegs = registrations.filter(r => r.status === 'ACTIVE');
  const retiredRegs = registrations.filter(r => r.status !== 'ACTIVE');

  const peoplePicker = (key: string) => (
    <select
      value={draftFor(key).userId}
      onChange={e => setDraftField(key, 'userId', e.target.value)}
      aria-label="Bind to person"
      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
    >
      <option value="">Select person</option>
      {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">SIM Registry</h2>
        <p className="text-sm text-gray-500">
          Which SIM belongs to whom. Calls made from an unbound SIM are flagged Unregistered;
          calls from a SIM bound to somebody else are flagged Mismatch. Android will not tell the
          app a SIM's own number, so the number below is the one you type here.
        </p>
      </div>

      {loadError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && (
      <>
      {/* ==================== Unclaimed ==================== */}
      <div className="mb-8">
        <h3 className="mb-1 text-xs font-medium uppercase text-gray-500">Unclaimed SIMs</h3>
        <p className="mb-2 text-xs text-gray-500">
          SIMs that have carried calls but nobody has bound yet.
        </p>
        <div className="rounded-lg border border-gray-200 bg-white">
          {unclaimed.map(sim => {
            const key = pairKey(sim.simSubscriptionId, sim.deviceInstallId);
            return (
              <div key={key} className="border-b border-gray-100 px-4 py-3 last:border-b-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="text-sm text-gray-900">
                      {sim.carrierName || 'Unknown carrier'}
                      {sim.slotIndex != null && <span className="text-gray-500"> · SIM slot {sim.slotIndex + 1}</span>}
                    </span>
                    <p className="text-xs text-gray-500">
                      {sim.callCount} calls · sub {sim.simSubscriptionId}
                      {sim.deviceInstallId ? ` · device ${sim.deviceInstallId}` : ''}
                    </p>
                    {sim.seenForUserIds.length > 0 && (
                      <p className="text-xs text-gray-500">
                        Used by {sim.seenForUserIds.map(id => personName(id)).join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      value={draftFor(key).phoneNumber}
                      onChange={e => setDraftField(key, 'phoneNumber', e.target.value)}
                      placeholder="Phone number"
                      aria-label="Phone number"
                      className="w-40 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    />
                    {peoplePicker(key)}
                    <button
                      type="button"
                      onClick={() => void bind(key, sim)}
                      disabled={busy === key || !sim.deviceInstallId || !draftFor(key).phoneNumber.trim() || !draftFor(key).userId}
                      className="flex items-center gap-1 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {busy === key ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Bind
                    </button>
                  </div>
                </div>
                {!sim.deviceInstallId && (
                  <p className="mt-1 text-xs text-amber-700">
                    Reported without a device id, so it cannot be bound. The app must resend it.
                  </p>
                )}
                {rowError[key] && <p className="mt-1 text-xs text-red-700">{rowError[key]}</p>}
              </div>
            );
          })}
          {unclaimed.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-400">No unclaimed SIMs.</p>
          )}
        </div>
      </div>

      {/* ==================== Registered ==================== */}
      <div>
        <h3 className="mb-1 text-xs font-medium uppercase text-gray-500">Registered SIMs</h3>
        <div className="rounded-lg border border-gray-200 bg-white">
          {[...activeRegs, ...retiredRegs].map(reg => {
            const isRetired = reg.status !== 'ACTIVE';
            const key = reg.id;
            return (
              <div key={reg.id} className="border-b border-gray-100 px-4 py-3 last:border-b-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className={`text-sm ${isRetired ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {reg.phoneNumber} — {personName(reg.userId, reg.user)}
                    </span>
                    <p className="text-xs text-gray-500">
                      {reg.carrierName || 'Unknown carrier'}
                      {reg.slotIndex != null ? ` · SIM slot ${reg.slotIndex + 1}` : ''}
                      {' · sub '}{reg.simSubscriptionId}
                      {isRetired ? ' · retired' : ''}
                    </p>
                  </div>
                  {!isRetired && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setReassigning(reassigning === reg.id ? null : reg.id)}
                        className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Repeat size={14} /> Reassign
                      </button>
                      <button
                        type="button"
                        onClick={() => void retire(reg)}
                        disabled={busy === reg.id}
                        className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      >
                        <Undo2 size={14} /> Retire
                      </button>
                    </div>
                  )}
                </div>
                {reassigning === reg.id && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                    <input
                      value={draftFor(key).phoneNumber}
                      onChange={e => setDraftField(key, 'phoneNumber', e.target.value)}
                      placeholder="Phone number"
                      aria-label="Phone number"
                      className="w-40 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    />
                    {peoplePicker(key)}
                    <button
                      type="button"
                      onClick={() => void bind(key, reg, reg.id)}
                      disabled={busy === key || !draftFor(key).phoneNumber.trim() || !draftFor(key).userId}
                      className="flex items-center gap-1 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {busy === key ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Save reassignment
                    </button>
                  </div>
                )}
                {rowError[key] && <p className="mt-1 text-xs text-red-700">{rowError[key]}</p>}
              </div>
            );
          })}
          {registrations.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-400">No registered SIMs yet.</p>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
